
import { useState, useRef } from 'react';
import { toast } from '@/hooks/use-toast';
import { readExcelFile } from '@/utils/excel/core/fileReader';
import { extractProductsFromSheet } from '@/utils/excel/parsers/productParser';

export const useFileHandling = () => {
  const [file, setFile] = useState<File | null>(null);
  const [previewData, setPreviewData] = useState<Array<{
    name: string;
    category: string | null;
    description: string | null;
    unit_of_measure?: string;
  }>>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [parseErrors, setParseErrors] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    setIsProcessing(true);
    setParseErrors([]);
    
    try {
      console.log('Reading Excel file:', selectedFile.name, selectedFile.type, selectedFile.size);
      const workbook = await readExcelFile(selectedFile);
      console.log('Workbook loaded with sheets:', workbook.SheetNames);
      
      if (workbook.SheetNames.length === 0) {
        throw new Error('The Excel file does not contain any sheets');
      }
      
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      console.log('Working with first sheet:', workbook.SheetNames[0]);
      
      if (!worksheet) {
        throw new Error('The first sheet in the Excel file is empty');
      }
      
      // Extract products with categories, descriptions, and measuring units
      const products = extractProductsFromSheet(worksheet);
      console.log('Extracted products:', products);
      
      if (products.length === 0) {
        toast({
          title: 'No valid products found',
          description: 'The file does not contain any valid product data. Please check your Excel file format and ensure product names are correctly entered.',
          variant: 'destructive',
        });
        handleReset();
        return;
      }
      
      // Set the parsed data for preview
      setPreviewData(products);
      
      toast({
        title: 'File processed successfully',
        description: `Found ${products.length} valid products in your file.`,
        variant: 'default',
      });
    } catch (error) {
      console.error('Error processing file:', error);
      const errorMessage = (error as Error).message || 'Failed to process the Excel file';
      setParseErrors([errorMessage]);
      
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
      handleReset();
    } finally {
      setIsProcessing(false);
    }
  };

  const handleReset = () => {
    setFile(null);
    setPreviewData([]);
    setParseErrors([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return {
    file,
    previewData,
    isProcessing,
    parseErrors,
    fileInputRef,
    handleFileChange,
    handleReset,
    setPreviewData
  };
};
